"use strict";
import { useEffect, useState } from "react";
import { useChainId } from "wagmi";
import { useAccount } from "./useAccount";
function getInjectedChainId() {
  if (typeof window === "undefined") {
    return void 0;
  }
  return window.ethereum?.chainId;
}
export function useProviderChainId() {
  const account = useAccount();
  const wagmiChainId = useChainId();
  const [detectedChainId, setDetectedChainId] = useState(wagmiChainId);
  const [isDetecting, setIsDetecting] = useState(true);
  useEffect(() => {
    if (!account.connector || !account.isConnected) {
      setDetectedChainId(wagmiChainId);
      setIsDetecting(false);
      return void 0;
    }
    let cleanup;
    const connector = account.connector;
    const setupListener = async () => {
      try {
        if (!connector || typeof connector.getProvider !== "function") {
          setDetectedChainId(wagmiChainId);
          setIsDetecting(false);
          return;
        }
        const provider = await connector.getProvider();
        const parseChainId = (chainId) => {
          if (!chainId) return void 0;
          if (typeof chainId === "number") {
            return chainId;
          }
          const chainIdStr = String(chainId);
          if (chainIdStr.startsWith("0x")) {
            return parseInt(chainIdStr, 16);
          }
          return parseInt(chainIdStr, 10);
        };
        if (provider?.chainId) {
          const currentChain = parseChainId(provider.chainId);
          if (currentChain) {
            const injectedChainId = getInjectedChainId();
            const ethereumChain = injectedChainId ? parseChainId(injectedChainId) : void 0;
            if (currentChain !== wagmiChainId && ethereumChain && ethereumChain === wagmiChainId) {
              setDetectedChainId(wagmiChainId);
            } else {
              setDetectedChainId(currentChain);
            }
          } else {
            console.warn("useProviderChainId: Failed to parse chainId, using wagmi fallback");
            setDetectedChainId(wagmiChainId);
          }
          setIsDetecting(false);
        } else {
          setDetectedChainId(wagmiChainId);
          setIsDetecting(false);
        }
        if (provider?.on) {
          const handleChainChanged = (chainIdValue) => {
            const newChainId = parseChainId(chainIdValue);
            if (newChainId) {
              const injectedChainId = getInjectedChainId();
              const ethereumChain = injectedChainId ? parseChainId(injectedChainId) : void 0;
              if (ethereumChain && ethereumChain !== newChainId) {
                setDetectedChainId(ethereumChain);
              } else {
                setDetectedChainId(newChainId);
              }
            }
          };
          provider.on("chainChanged", handleChainChanged);
          cleanup = () => {
            provider.removeListener?.("chainChanged", handleChainChanged);
          };
        }
      } catch (error) {
        console.error("useProviderChainId: Error setting up chain listener:", error);
        setDetectedChainId(wagmiChainId);
        setIsDetecting(false);
      }
    };
    setupListener();
    return () => {
      cleanup?.();
    };
  }, [account.connector, account.isConnected, wagmiChainId]);
  return {
    chainId: detectedChainId ?? wagmiChainId,
    isDetecting
  };
}
